import { useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useSnackbar } from "notistack";
import { Alert, Box, Button, CircularProgress, Typography } from "@mui/material";
import { errorMessageOf, FURGONETKA_OAUTH_STORAGE_KEY, useFurgonetkaAccount } from "../../../../api/shipping";
import { ERROR_SNACKBAR, SUCCESS_SNACKBAR } from "../../../../consts";
import { TENANT_ROUTES } from "../../tenantConfig.routes";

const readStoredAttempt = (): { state?: string; tenantId?: string } => {
  try {
    return JSON.parse(sessionStorage.getItem(FURGONETKA_OAUTH_STORAGE_KEY) || "{}");
  } catch {
    return {};
  }
};

/** Where Furgonetka sends the user back. The code it brings is valid for 30
 * seconds, so it goes to the backend straight away, with no extra click. */
export const FurgonetkaCallback = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const [searchParams] = useSearchParams();
  const { connect } = useFurgonetkaAccount();
  const [error, setError] = useState<string>("");
  // React's strict mode runs effects twice in development; the code is
  // single-use, so it is only ever sent once.
  const sent = useRef(false);

  useEffect(() => {
    if (sent.current) return;
    sent.current = true;

    const code = searchParams.get("code");
    const state = searchParams.get("state");
    const stored = readStoredAttempt();
    try {
      sessionStorage.removeItem(FURGONETKA_OAUTH_STORAGE_KEY);
    } catch {
      // Nothing to clean up.
    }

    if (!code || !state) {
      setError(searchParams.get("error_description") || t("Furgonetka did not approve the connection."));
      return;
    }
    if (stored.state && stored.state !== state) {
      setError(t("This connection was not started from this browser. Try connecting again."));
      return;
    }

    const finish = async () => {
      const { success, errorData } = await connect(code, state, stored.tenantId);
      if (success) {
        enqueueSnackbar(t("Furgonetka account connected."), SUCCESS_SNACKBAR);
        navigate(TENANT_ROUTES.integrations, { replace: true });
      } else {
        const message = errorMessageOf(errorData, t("Could not connect the Furgonetka account."));
        enqueueSnackbar(message, ERROR_SNACKBAR);
        setError(message);
      }
    };
    finish();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 2, p: 4 }}>
      {error ? (
        <>
          <Alert severity="error">{error}</Alert>
          <Button variant="outlined" onClick={() => navigate(TENANT_ROUTES.integrations, { replace: true })}>
            {t("Back to integrations")}
          </Button>
        </>
      ) : (
        <>
          <CircularProgress />
          <Typography variant="body2" color="text.secondary">
            {t("Connecting your Furgonetka account...")}
          </Typography>
        </>
      )}
    </Box>
  );
};
